import { api } from '@/helpers/apiClient'
import logger, { toErrorMessage } from '@/helpers/logger'
import {
  inviteDtoSchema,
  inviteListDtoSchema,
  invitePreviewDtoSchema,
  type InviteDto,
  type InviteListDto,
  type InvitePreviewDto,
} from '@lib/dto/invite.dto'
import {
  loginResponseDtoSchema,
  type LoginResponseDto,
} from '@lib/dto/auth.dto'
import type {
  AcceptInviteInput,
  CreateInviteInput,
} from '@lib/input/invite.input'
import type { IInviteService } from '@/types/InviteService.types'

/**
 * Service for the invite REST endpoints. Responses are validated against the
 * shared `@lib` schemas at the boundary, so a drift between the API and the
 * expected shape surfaces as a clean error here rather than a `TypeError`
 * deeper in the UI.
 *
 * Tokens are passed through `encodeURIComponent` before hitting the path: they
 * are base64url and so URL-safe in practice, but a hand-mangled token from the
 * address bar must not be able to alter the request path.
 */
class InviteService implements IInviteService {
  async list(): Promise<InviteListDto> {
    try {
      return await api.get('/invites', inviteListDtoSchema)
    } catch (error) {
      logger.error('Failed to list invites', { error: toErrorMessage(error) })
      throw error
    }
  }

  async create(input: CreateInviteInput): Promise<InviteDto> {
    try {
      return await api.post('/invites', input, inviteDtoSchema)
    } catch (error) {
      logger.error('Failed to create invite', { error: toErrorMessage(error) })
      throw error
    }
  }

  async revoke(id: string): Promise<InviteDto> {
    try {
      return await api.post(
        `/invites/${encodeURIComponent(id)}/revoke`,
        {},
        inviteDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to revoke invite', {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async preview(token: string): Promise<InvitePreviewDto> {
    try {
      return await api.get(
        `/invites/accept/${encodeURIComponent(token)}`,
        invitePreviewDtoSchema,
      )
    } catch (error) {
      // Deliberately does not log the token — it is a live credential.
      logger.error('Failed to resolve invite token', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async accept(
    token: string,
    input: AcceptInviteInput,
  ): Promise<LoginResponseDto> {
    try {
      return await api.post(
        `/invites/accept/${encodeURIComponent(token)}`,
        input,
        loginResponseDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to accept invite', { error: toErrorMessage(error) })
      throw error
    }
  }
}

export const inviteService = new InviteService()
